import { createAsyncThunk } from '@reduxjs/toolkit';
import { Platform } from 'react-native';
import { HomeReview, ReviewContactDetails, ReviewItem, ReviewTemplate, ReviewResponse } from '../../types';
import { reviewService } from '../../services/reviewService';
import { api } from '../../services/api';
import { FULL_REVIEW_TEMPLATES_PATH } from '../../services/templateApiPaths';
import { getOfflineReviewService, getCacheService, getOfflineDatabase } from '../../services/platformServices';
import { syncService } from '../../services/syncService';
import { shouldSkipOfflineCache } from '../../config/environments';
import { shouldUseOfflineReviewWorkflow } from '../../config/offlineWorkflow';
import { getEffectiveNetworkOnline } from '../../utils/detoxTestControls';

const offlineReviewService = getOfflineReviewService();
const cacheService = getCacheService();
const offlineDatabase = getOfflineDatabase();

const ensureOfflineReviewServiceInitialized = async () => {
  if (offlineReviewService && !offlineReviewService.isServiceInitialized()) {
    await offlineReviewService.initialize();
  }
};

const findItemInReviewTemplate = (review: HomeReview, itemId: number): ReviewItem | null => {
  const categories = review.template?.categories || [];
  const search = (nodes: any[]): ReviewItem | null => {
    for (const category of nodes) {
      const item = category?.items?.find((candidate: ReviewItem) =>
        Number(candidate?.id) === Number(itemId)
      );
      if (item) {
        return item;
      }

      const nested = search(category?.subcategories || []);
      if (nested) {
        return nested;
      }
    }

    return null;
  };

  return search(categories);
};

const getOfflineReviewResponse = (review: HomeReview, itemId: number): ReviewResponse | null =>
  review.responses?.find(response => Number(response.itemId) === Number(itemId)) || null;

type UpdateLocalResponsePayload = {
  reviewId: number;
  itemId: number;
  data: Partial<ReviewResponse>;
};

const updateLocalResponseAction = (payload: UpdateLocalResponsePayload) => ({
  type: 'review/updateLocalResponse' as const,
  payload,
});

export const fetchReviews = createAsyncThunk(
  'review/fetchReviews',
  async () => {
    try {
      // Initialize offline service if needed (only on mobile)
      if (offlineReviewService && !offlineReviewService.isServiceInitialized()) {
        await offlineReviewService.initialize();
      }

      const reviews = await reviewService.getUserReviews();

      if (!shouldSkipOfflineCache) {
        await reviewService.refreshTemplateCache();
      }

      // Preload historical template snapshots for offline usage
      if (!shouldSkipOfflineCache && Array.isArray(reviews) && reviews.length > 0) {
        try {
          await reviewService.refreshTemplateCache();
        } catch (err) {
          console.warn('[ReviewSlice] Failed to refresh template cache:', err);
        }

        try {
          await reviewService.preloadReviewSnapshots(reviews);
        } catch (err) {
          console.warn('[ReviewSlice] Failed to preload snapshots:', err);
        }
      }

      return reviews;
    } catch (error) {
      throw error;
    }
  }
);

export const fetchReviewById = createAsyncThunk(
  'review/fetchReviewById',
  async (
    {
      reviewId,
      includeTemplate = true,
    }: {
      reviewId: number;
      includeTemplate?: boolean;
    }
  ) => {
    return await reviewService.getReviewDetails(reviewId, {
      includePhotos: false,
      presignPhotos: false,
      includeTemplate,
    });
  }
);

export const fetchReviewItemDetail = createAsyncThunk(
  'review/fetchReviewItemDetail',
  async (
    {
      reviewId,
      itemId,
      persistResponse = true,
    }: {
      reviewId: number;
      itemId: number;
      persistResponse?: boolean;
    },
    { dispatch }
  ) => {
    if (shouldUseOfflineReviewWorkflow() && offlineReviewService?.getOfflineReview) {
      const offlineReview = await offlineReviewService.getOfflineReview(reviewId);
      const offlineItem = findItemInReviewTemplate(offlineReview, itemId);
      const offlineResponse = getOfflineReviewResponse(offlineReview, itemId);

      if (offlineResponse && persistResponse) {
        dispatch(updateLocalResponseAction({
          reviewId,
          itemId,
          data: offlineResponse,
        }));
      }

      if (offlineItem) {
        return {
          item: offlineItem,
          response: offlineResponse,
          photoPresignFailed: false,
        };
      }
    }

    const result = await reviewService.getReviewItemDetail(reviewId, itemId);
    const responseData = result.response;

    let photosWithUrls = responseData?.photos || [];
    let photoPresignFailed = false;

    if (responseData?.photos && responseData.photos.length > 0) {
      try {
        const presigned = await reviewService.getPhotoPresignedUrls(responseData.photos.map(photo => photo.id));
        const presignedMap = new Map(presigned.map(entry => [entry.photoId, entry.presignedUrl]));
        photosWithUrls = responseData.photos.map(photo => ({
          ...photo,
          presignedUrl: presignedMap.get(photo.id),
        }));
      } catch {
        // Photo URLs are optional presentation data. Keep the successfully
        // fetched business response usable when signing is temporarily down.
        photoPresignFailed = true;
      }
    }

    if (responseData && persistResponse) {
      dispatch(updateLocalResponseAction({
        reviewId,
        itemId,
        data: {
          ...responseData,
          photos: photosWithUrls,
        },
      }));
    }

    return {
      item: result.item,
      response: responseData ? { ...responseData, photos: photosWithUrls } : null,
      photoPresignFailed,
    };
  }
);

export const fetchTemplates = createAsyncThunk(
  'review/fetchTemplates',
  async (forceRefresh: boolean = false) => {
    if (Platform.OS !== 'web' && shouldSkipOfflineCache) {
      return await api.get<ReviewTemplate[]>(FULL_REVIEW_TEMPLATES_PATH);
    }

    try {
      // Initialize cache service if needed
      if (!cacheService.getConfiguration()) {
        await cacheService.initialize();
      }

      return await cacheService.getTemplates(forceRefresh);
    } catch (error) {
      // Fallback to direct service call
      return await reviewService.getTemplates();
    }
  }
);

export const fetchTemplateDetails = createAsyncThunk(
  'review/fetchTemplateDetails',
  async (templateId: number) => {
    return await reviewService.getTemplateDetails(templateId);
  }
);

export const createReview = createAsyncThunk(
  'review/createReview',
  async (
    {
      propertyId,
      templateId,
      address,
      memberName,
      memberEmail,
      homeManagerName,
      homeManagerEmail,
    }: {
      propertyId: number;
      templateId: number;
      address?: string;
    } & ReviewContactDetails
  ) => {
    try {
      const details: ReviewContactDetails = {
        memberName,
        memberEmail,
        homeManagerName,
        homeManagerEmail,
      };

      if (shouldSkipOfflineCache) {
        return await reviewService.createReview(
          propertyId,
          templateId,
          address,
          details,
          { forceOnline: true }
        );
      }

      const useOfflineWorkflow = shouldUseOfflineReviewWorkflow();
      if (useOfflineWorkflow) {
        await ensureOfflineReviewServiceInitialized();
      }

      const isOnline = await getEffectiveNetworkOnline();
      if (!isOnline) {
        // Initialize offline service if needed (only on mobile)
        await ensureOfflineReviewServiceInitialized();

        const offlineReview = offlineReviewService
          ? await offlineReviewService.createOfflineReview(propertyId, templateId, address, details)
          : null;

        return offlineReview;
      }

      // Online creation
      const review = await reviewService.createReview(
        propertyId,
        templateId,
        address,
        details,
        { networkOnline: isOnline }
      );
      return review;
    } catch (error) {
      console.error('[ReviewSlice] Failed to create review:', error);
      throw error;
    }
  }
);

export const updateReviewResponse = createAsyncThunk(
  'review/updateResponse',
  async (
    {
      reviewId,
      itemId,
      data,
      optimistic = true,
      syncStatus,
      enableAutoSave = true,
    }: {
      reviewId: number;
      itemId: number;
      data: Partial<ReviewResponse>;
      optimistic?: boolean;
      syncStatus?: 'pending' | 'synced' | 'failed';
      enableAutoSave?: boolean;
    },
    { dispatch, getState }
  ) => {
    try {
      // Apply optimistic update first if enabled
      if (optimistic) {
        dispatch(updateLocalResponseAction({ reviewId, itemId, data }));
      }

      const shouldWriteLocally = shouldUseOfflineReviewWorkflow();
      const isOnline = shouldWriteLocally ? true : await getEffectiveNetworkOnline();

      if (shouldWriteLocally || !isOnline) {
        // Initialize offline service if needed (only on mobile)
        await ensureOfflineReviewServiceInitialized();

        if (offlineReviewService?.cacheOnlineReview && offlineDatabase?.getReview) {
          try {
            const existingCachedReview = await offlineDatabase.getReview(reviewId);
            if (!existingCachedReview) {
              const state = getState() as any;
              const reviewToCache = state.review?.reviews?.find((entry: HomeReview) => Number(entry.id) === Number(reviewId));
              if (reviewToCache) {
                await offlineReviewService.cacheOnlineReview(reviewToCache);
              }
            }
          } catch {
            // If cache priming fails, keep the response update path in charge of surfacing errors.
          }
        }

        const response = offlineReviewService
          ? await offlineReviewService.updateOfflineResponse(
              reviewId,
              itemId,
              data,
              enableAutoSave,
              syncStatus ?? 'pending'
            )
          : null;

        return response;
      }

      // Online update
      const response = await reviewService.updateReviewResponse(
        reviewId,
        itemId,
        data,
        shouldSkipOfflineCache ? { forceOnline: true } : undefined
      );

      // Update offline cache so app restart shows correct status
      // Do this after successful server update to ensure offline DB stays fresh
      if (offlineReviewService?.updateOfflineResponse) {
        try {
          // Pass syncStatus='synced' to mark as synchronized (not pending)
          // Pass enableAutoSave=false to skip auto-save scheduling
          await offlineReviewService.updateOfflineResponse(reviewId, itemId, response, false, 'synced');
        } catch (cacheError) {
          // Don't throw - cache update failure shouldn't break the main flow
        }
      }

      return response;
    } catch (error) {
      // Revert optimistic update on error if it was applied
      if (optimistic) {
        // Note: In a real implementation, you might want to store the previous state
        // and revert to it here. For now, we'll let the UI handle the error state.
      }

      throw error;
    }
  }
);

export const updateSubmittedReviewResponse = createAsyncThunk(
  'review/updateSubmittedResponse',
  async ({
    reviewId,
    itemId,
    data,
  }: {
    reviewId: number;
    itemId: number;
    data: Partial<ReviewResponse>;
  }) => {
    try {
      if (shouldUseOfflineReviewWorkflow()) {
        await ensureOfflineReviewServiceInitialized();
        return offlineReviewService
          ? await offlineReviewService.updateOfflineResponse(reviewId, itemId, data)
          : null;
      }

      const result = await reviewService.updateSubmittedResponse(reviewId, itemId, data);

      // Update offline cache after successful update
      if (offlineReviewService?.updateOfflineResponse) {
        try {
          // Pass syncStatus='synced' to mark as synchronized (not pending)
          // Pass enableAutoSave=false to skip auto-save scheduling
          await offlineReviewService.updateOfflineResponse(reviewId, itemId, result, false, 'synced');
        } catch (cacheError) {
          // Don't throw - cache update failure shouldn't break the main flow
        }
      }

      return result;
    } catch (error) {
      console.error('updateSubmittedReviewResponse thunk failed:', error);
      throw error;
    }
  }
);

export const submitReview = createAsyncThunk(
  'review/submitReview',
  async ({ reviewId, vendorNotes }: { reviewId: number; vendorNotes?: string }) => {
    try {
      if (shouldUseOfflineReviewWorkflow()) {
        await ensureOfflineReviewServiceInitialized();
        const updatedReview = offlineReviewService?.queueReviewSubmission
          ? await offlineReviewService.queueReviewSubmission(reviewId, vendorNotes)
          : await offlineReviewService?.updateOfflineReview(reviewId, {
              status: 'submitted',
              submittedAt: new Date(),
              vendorNotes,
            });

        return {
          message: 'Review queued for submission during the next sync',
          review: updatedReview,
        };
      }

      // Check network status
      const isOnline = shouldSkipOfflineCache || await getEffectiveNetworkOnline();

      if (!isOnline) {

        // Initialize offline service if needed (only on mobile)
        await ensureOfflineReviewServiceInitialized();

        // Update review status locally (optimistic update)
        const updatedReview = offlineReviewService
          ? await offlineReviewService.updateOfflineReview(reviewId, {
              status: 'submitted',
              submittedAt: new Date(),
              vendorNotes: vendorNotes,
            })
          : null;

        // Queue submission for sync when online
        await syncService.queueReviewSubmission(reviewId.toString());

        // Return offline review in the expected format
        return {
          message: 'Review queued for submission when online',
          review: updatedReview,
        };
      }

      // CRITICAL: Ensure all pending responses are synced before submitting
      // This prevents the race condition where submission completes before offline responses sync
      if (!shouldSkipOfflineCache && Platform.OS !== 'web' && offlineReviewService?.isServiceInitialized()) {
        try {
          const syncStats = await offlineReviewService.getSyncStats();
          const hasPendingData =
            syncStats.pendingReviews > 0 ||
            syncStats.pendingResponses > 0 ||
            syncStats.pendingPhotos > 0 ||
            syncStats.recoveryAvailable ||
            syncStats.syncErrors > 0;

          if (hasPendingData) {
            console.warn('[ReviewSlice] Syncing pending responses before submit...', syncStats);

            // Wait for sync to complete with timeout
            const syncPromise = offlineReviewService.performFullSync();
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Pre-submit sync timeout')), 30000)
            );

            try {
              await Promise.race([syncPromise, timeoutPromise]);
              console.warn('[ReviewSlice] Pre-submit sync completed successfully');
            } catch (syncError) {
              console.warn('[ReviewSlice] Pre-submit sync failed or timed out:', syncError);
              // Continue with submission - server will validate required items
            }
          }
        } catch (statsError) {
          console.warn('[ReviewSlice] Failed to check sync stats before submit:', statsError);
          // Continue with submission
        }
      }

      // Online submission
      const result = await reviewService.submitReview(reviewId, vendorNotes);

      // Cache the submitted review offline for sharing (only on mobile)
      if (!shouldSkipOfflineCache && offlineReviewService?.cacheOnlineReview && result.review) {
        try {
          await offlineReviewService.cacheOnlineReview(result.review);
        } catch (cacheError) {
          console.error('[ReviewSlice] Cache failed after submit:', cacheError);
          // Don't fail submission if caching fails
        }
      }

      return result;
    } catch (error) {
      console.error('[ReviewSlice] Failed to submit review:', error);
      throw error;
    }
  }
);

export const deleteReview = createAsyncThunk(
  'review/deleteReview',
  async (reviewId: number) => {
    // Check network status - deletion requires internet connection
    const isOnline = await getEffectiveNetworkOnline();

    if (!isOnline) {
      throw new Error('Cannot delete review while offline. Please connect to the internet and try again.');
    }

    await reviewService.deleteReview(reviewId);
    return reviewId;
  }
);

export const completeReview = createAsyncThunk(
  'review/completeReview',
  async (reviewId: number) => {
    try {
      if (shouldUseOfflineReviewWorkflow()) {
        await ensureOfflineReviewServiceInitialized();
        const updatedReview = offlineReviewService?.queueReviewCompletion
          ? await offlineReviewService.queueReviewCompletion(reviewId)
          : await offlineReviewService?.updateOfflineReview(reviewId, {
              status: 'completed',
              completedAt: new Date(),
            });

        return {
          message: 'Review completion queued for the next sync',
          review: updatedReview,
        };
      }

      // Check network status
      const isOnline = shouldSkipOfflineCache || await getEffectiveNetworkOnline();

      if (!isOnline) {
        // Initialize offline service if needed (only on mobile)
        await ensureOfflineReviewServiceInitialized();

        // Update review status locally (optimistic update)
        const updatedReview = offlineReviewService
          ? await offlineReviewService.updateOfflineReview(reviewId, {
              status: 'completed',
              completedAt: new Date(),
            })
          : null;

        // Queue completion for sync when online
        await syncService.queueReviewCompletion(reviewId.toString());

        // Return offline review in the expected format
        return {
          message: 'Review completion queued for sync when online',
          review: updatedReview,
        };
      }

      // CRITICAL: Ensure all pending responses are synced before completing
      // This prevents the race condition where completion completes before offline responses sync
      if (!shouldSkipOfflineCache && Platform.OS !== 'web' && offlineReviewService?.isServiceInitialized()) {
        try {
          const syncStats = await offlineReviewService.getSyncStats();
          const hasPendingData =
            syncStats.pendingReviews > 0 ||
            syncStats.pendingResponses > 0 ||
            syncStats.pendingPhotos > 0 ||
            syncStats.recoveryAvailable ||
            syncStats.syncErrors > 0;

          if (hasPendingData) {
            console.warn('[ReviewSlice] Syncing pending responses before complete...', syncStats);

            // Wait for sync to complete with timeout
            const syncPromise = offlineReviewService.performFullSync();
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Pre-complete sync timeout')), 30000)
            );

            try {
              await Promise.race([syncPromise, timeoutPromise]);
              console.warn('[ReviewSlice] Pre-complete sync completed successfully');
            } catch (syncError) {
              console.warn('[ReviewSlice] Pre-complete sync failed or timed out:', syncError);
              // Continue with completion - server will validate required items
            }
          }
        } catch (statsError) {
          console.warn('[ReviewSlice] Failed to check sync stats before complete:', statsError);
          // Continue with completion
        }
      }

      // Online completion - Call the API to mark review as complete
      const result = await reviewService.completeReview(reviewId);

      // Cache the completed review offline for sharing (only on mobile)
      if (!shouldSkipOfflineCache && offlineReviewService?.cacheOnlineReview && result.review) {
        try {
          await offlineReviewService.cacheOnlineReview(result.review);
        } catch (cacheError) {
          // Don't fail completion if caching fails
        }
      }

      return result;
    } catch (error) {
      console.error('[ReviewSlice] Failed to complete review:', error);
      throw error;
    }
  }
);

// On-demand items thunks
export const fetchOnDemandItems = createAsyncThunk(
  'review/fetchOnDemandItems',
  async (reviewId: number) => {
    return await reviewService.getOnDemandItems(reviewId);
  }
);

export const createOnDemandItem = createAsyncThunk(
  'review/createOnDemandItem',
  async ({ reviewId, data }: { reviewId: number; data: any }) => {
    return await reviewService.createOnDemandItem(reviewId, data);
  }
);

export const updateOnDemandItem = createAsyncThunk(
  'review/updateOnDemandItem',
  async ({ reviewId, itemId, data }: { reviewId: number; itemId: number; data: any }) => {
    return await reviewService.updateOnDemandItem(reviewId, itemId, data);
  }
);

export const deleteOnDemandItem = createAsyncThunk(
  'review/deleteOnDemandItem',
  async ({ reviewId, itemId }: { reviewId: number; itemId: number }) => {
    await reviewService.deleteOnDemandItem(reviewId, itemId);
    return { reviewId, itemId };
  }
);

export const updateReviewNotes = createAsyncThunk(
  'review/updateNotes',
  async (
    { reviewId, notes }: { reviewId: number; notes: string }
  ) => {
    try {
      if (shouldUseOfflineReviewWorkflow()) {
        await ensureOfflineReviewServiceInitialized();
        return offlineReviewService
          ? await offlineReviewService.updateOfflineReview(reviewId, { notes })
          : null;
      }

      // Check network status
      const isOnline = await getEffectiveNetworkOnline();

      if (!isOnline) {
        // Initialize offline service if needed (only on mobile)
        await ensureOfflineReviewServiceInitialized();

        const updatedReview = offlineReviewService
          ? await offlineReviewService.updateOfflineReview(reviewId, { notes })
          : null;

        return updatedReview;
      }

      // Online update
      return await reviewService.updateReviewNotes(reviewId, notes);
    } catch (error) {
      throw error;
    }
  }
);

export const updateReviewOnDemandProjects = createAsyncThunk(
  'review/updateOnDemandProjects',
  async (
    { reviewId, onDemandProjects }: { reviewId: number; onDemandProjects: string }
  ) => {
    try {
      if (shouldUseOfflineReviewWorkflow()) {
        await ensureOfflineReviewServiceInitialized();
        return offlineReviewService
          ? await offlineReviewService.updateOfflineReview(reviewId, { onDemandProjects })
          : null;
      }

      const isOnline = await getEffectiveNetworkOnline();

      if (!isOnline) {
        await ensureOfflineReviewServiceInitialized();

        const updatedReview = offlineReviewService
          ? await offlineReviewService.updateOfflineReview(reviewId, { onDemandProjects })
          : null;

        return updatedReview;
      }

      return await reviewService.updateReviewOnDemandProjects(reviewId, onDemandProjects);
    } catch (error) {
      throw error;
    }
  }
);

export const updateReviewDetails = createAsyncThunk(
  'review/updateDetails',
  async ({
    reviewId,
    details,
  }: {
    reviewId: number;
    details: ReviewContactDetails;
  }) => {
    if (!details.memberName.trim()) {
      throw new Error('Member name is required');
    }

    if (shouldUseOfflineReviewWorkflow()) {
      await ensureOfflineReviewServiceInitialized();
      if (!offlineReviewService?.updateOfflineReview) {
        throw new Error('Offline review service not available');
      }
      return offlineReviewService.updateOfflineReview(reviewId, details);
    }

    const isOnline = await getEffectiveNetworkOnline();
    if (!isOnline) {
      await ensureOfflineReviewServiceInitialized();
      if (!offlineReviewService?.updateOfflineReview) {
        throw new Error('Offline review service not available');
      }
      return offlineReviewService.updateOfflineReview(reviewId, details);
    }

    return reviewService.updateReviewDetails(reviewId, details);
  }
);

// ====================
// OFFLINE-SPECIFIC THUNKS
// ====================

/**
 * Initialize offline services
 */
export const initializeOfflineServices = createAsyncThunk(
  'review/initializeOfflineServices',
  async () => {
    try {
      // Initialize services only on mobile
      if (cacheService && cacheService.initialize) {
        await cacheService.initialize();
      }
      if (offlineReviewService && offlineReviewService.initialize) {
        await offlineReviewService.initialize();
      }

      return true;
    } catch (error) {
      throw error;
    }
  }
);

/**
 * Sync all pending offline data
 */
export const syncOfflineData = createAsyncThunk(
  'review/syncOfflineData',
  async (_, { dispatch }) => {
    try {
      // Initialize services if needed (only on mobile)
      if (offlineReviewService && !offlineReviewService.isServiceInitialized()) {
        await offlineReviewService.initialize();
      }

      // Sync pending review data (only on mobile)
      if (offlineReviewService && offlineReviewService.performFullSync) {
        await offlineReviewService.performFullSync();
      } else if (offlineReviewService && offlineReviewService.syncPendingReviews) {
        await offlineReviewService.syncPendingReviews();
      }

      let syncStats = null;
      if (offlineReviewService && offlineReviewService.getSyncStats) {
        syncStats = await offlineReviewService.getSyncStats();
      }

      // Refresh reviews after sync
      await dispatch(fetchReviews());

      return syncStats;
    } catch (error) {
      throw error;
    }
  }
);

/**
 * Force save all drafts
 */
export const saveAllDrafts = createAsyncThunk(
  'review/saveAllDrafts',
  async () => {
    try {
      // Only on mobile
      if (offlineReviewService && !offlineReviewService.isServiceInitialized()) {
        await offlineReviewService.initialize();
      }

      if (offlineReviewService && offlineReviewService.saveAllDrafts) {
        await offlineReviewService.saveAllDrafts();
      }

      return true;
    } catch (error) {
      throw error;
    }
  }
);
